import { theme } from '../../config/theme';

// Re-export theme colors for backward compatibility
export const KOLEGA_GREEN = theme.colors.brand.primary;
export const KOLEGA_GREEN_DARKER = theme.colors.brand.primaryDark;
export const KOLEGA_DARK_GREEN = theme.colors.brand.primaryDarkMode;
export const KOLEGA_TEXT_DARK = theme.colors.ui.text.primary.light;

// Background colors
export const BG_LIGHT = theme.colors.ui.background.secondary.light;
export const BG_DARK = theme.colors.ui.background.primary.dark;

// UI element colors
export const UI_LIGHT = theme.colors.ui.background.secondary.light;
export const UI_DARK = theme.colors.ui.background.secondary.dark;

// Border colors
export const BORDER_LIGHT = theme.colors.ui.border.primary.light;
export const BORDER_DARK = theme.colors.ui.border.primary.dark;

// Message background colors - Light mode
export const MSG_SYSTEM_LIGHT = theme.colors.agents.system.light;
export const MSG_AGENT_LIGHT = theme.colors.agents.investigation.light;
export const MSG_CODER_LIGHT = theme.colors.agents.coder.light;
export const MSG_TASK_LIGHT = theme.colors.agents.taskPlanning.light;
export const MSG_DEFAULT_LIGHT = theme.colors.agents.default.light;

// Message background colors - Dark mode
export const MSG_SYSTEM_DARK = theme.colors.agents.system.dark;
export const MSG_AGENT_DARK = theme.colors.agents.investigation.dark;
export const MSG_CODER_DARK = theme.colors.agents.coder.dark;
export const MSG_TASK_DARK = theme.colors.agents.taskPlanning.dark;
export const MSG_DEFAULT_DARK = theme.colors.agents.default.dark;

// This styling is for custom animations and elements not covered by Tailwind
export const styling = `
  /* Fixed sizing and overflow styles */
  .main-container {
    height: calc(100vh - 88px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  
  .chat-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }
  
  .message-container {
    flex: 1;
    overflow-y: auto;
    max-height: calc(100vh - 200px); /* Account for header, input area, tabs */
  }
  
  .tools-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }
  
  .tools-content {
    flex: 1;
    overflow: hidden;
    height: calc(100vh - 144px); /* Account for header and tabs */
  }
  
  .tools-tab-content {
    height: 100%;
    overflow-y: auto;
    max-height: 100%;
  }
  
  /* Improve typing animation */
  @keyframes kolegaPulse {
    0% { opacity: 0.2; }
    50% { opacity: 1; }
    100% { opacity: 0.2; }
  }
  
  .kolega-animate-pulse {
    animation: kolegaPulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite !important;
  }
  
  .animation-delay-200 {
    animation-delay: 0.2s !important;
  }
  
  .animation-delay-400 {
    animation-delay: 0.4s !important;
  }
  
  /* Prevent text selection during resize */
  .resize-active {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }
  
  /* Custom appearance for the divider */
  .resizer {
    background-color: ${theme.colors.ui.background.tertiary.light};
    cursor: col-resize;
    height: 100%;
    width: 8px;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 10;
    transition: background-color 0.2s;
  }
  
  .dark .resizer {
    background-color: ${theme.colors.ui.background.tertiary.dark};
  }
  
  .resizer:hover,
  .resizer.dragging {
    background-color: ${KOLEGA_GREEN};
  }
  
  .dark .resizer:hover,
  .dark .resizer.dragging {
    background-color: ${KOLEGA_DARK_GREEN};
  }
  
  .resizer::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 2px;
    height: 30px;
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 1px;
  }
  
  .dark .resizer::after {
    background-color: rgba(255, 255, 255, 0.2);
  }
  
  /* Custom scrollbar */
  .message-container::-webkit-scrollbar {
    width: 8px;
  }
  
  .message-container::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .message-container::-webkit-scrollbar-thumb {
    background-color: ${BORDER_LIGHT};
    border-radius: 4px;
  }
  
  .dark .message-container::-webkit-scrollbar-thumb {
    background-color: ${BORDER_DARK};
  }
  
  .message-container::-webkit-scrollbar-thumb:hover {
    background-color: ${KOLEGA_GREEN_DARKER};
  }
  
  /* Dark mode support for markdown content */
  .dark .markdown-content {
    color: var(--color-text-primary);
  }
  
  .dark .markdown-content h1,
  .dark .markdown-content h2,
  .dark .markdown-content h3,
  .dark .markdown-content h4,
  .dark .markdown-content h5,
  .dark .markdown-content h6 {
    color: var(--color-text-primary);
    border-bottom: 1px solid ${BORDER_DARK};
  }
  
  .dark .markdown-content a {
    color: ${KOLEGA_GREEN};
  }
  
  .dark .markdown-content blockquote {
    border-left-color: ${BORDER_DARK};
    color: var(--color-text-secondary);
    background-color: rgba(0, 0, 0, 0.1);
  }
  
  .dark .markdown-content code {
    background-color: ${UI_DARK};
    color: var(--color-text-secondary);
  }
  
  .dark .markdown-content pre {
    background-color: ${UI_DARK};
    border: 1px solid ${BORDER_DARK};
  }
  
  .dark .markdown-content tr {
    border-top-color: ${BORDER_DARK};
  }
  
  .dark .markdown-content th,
  .dark .markdown-content td {
    border-color: ${BORDER_DARK};
  }
  
  .dark .markdown-content hr {
    border-color: ${BORDER_DARK};
  }
  
  /* Light mode support for markdown */
  .markdown-content h1,
  .markdown-content h2,
  .markdown-content h3,
  .markdown-content h4,
  .markdown-content h5,
  .markdown-content h6 {
    color: ${KOLEGA_TEXT_DARK};
    border-bottom: 1px solid ${BORDER_LIGHT};
  }
  
  .markdown-content a {
    color: ${KOLEGA_DARK_GREEN};
  }
  
  .markdown-content blockquote {
    border-left: 3px solid ${KOLEGA_GREEN};
    padding-left: 1em;
    color: var(--color-text-secondary);
    background-color: rgba(0, 0, 0, 0.05);
  }
  
  .markdown-content code {
    background-color: ${MSG_SYSTEM_LIGHT};
    color: ${KOLEGA_TEXT_DARK};
    padding: 0.2em 0.4em;
    border-radius: 3px;
  }
  
  .markdown-content pre {
    background-color: ${UI_LIGHT};
    padding: 1em;
    border-radius: 4px;
    border: 1px solid ${BORDER_LIGHT};
  }
  
  .markdown-content pre code {
    background-color: transparent;
    padding: 0;
  }
  
  .markdown-content table {
    border-collapse: collapse;
    width: 100%;
    margin: 1em 0;
  }
  
  .markdown-content th,
  .markdown-content td {
    border: 1px solid ${BORDER_LIGHT};
    padding: 8px;
    text-align: left;
  }
  
  .markdown-content tr:nth-child(even) {
    background-color: ${MSG_SYSTEM_LIGHT};
  }
  
  .dark .markdown-content tr:nth-child(even) {
    background-color: ${MSG_SYSTEM_DARK};
  }
`;

export default {
  KOLEGA_GREEN,
  KOLEGA_GREEN_DARKER,
  KOLEGA_DARK_GREEN,
  KOLEGA_TEXT_DARK,
  BG_LIGHT,
  BG_DARK,
  UI_LIGHT,
  UI_DARK,
  BORDER_LIGHT,
  BORDER_DARK,
  MSG_SYSTEM_LIGHT,
  MSG_AGENT_LIGHT,
  MSG_CODER_LIGHT,
  MSG_TASK_LIGHT,
  MSG_DEFAULT_LIGHT,
  MSG_SYSTEM_DARK,
  MSG_AGENT_DARK,
  MSG_CODER_DARK,
  MSG_TASK_DARK,
  MSG_DEFAULT_DARK,
  styling
};